import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectTeamIndex } from '../../../../store/user/user.selector.js';
import { selectCriticalMessage } from '../../../../store/system/system.selector.js';
import { setSystemNotification } from '../../../../store/system/system.action.js';
import { setCriticalMessage } from '../../../../store/system/system.action.js';
// hooks
import { useCreateAccount } from '../../../../hooks/useCreateAccount';
import { useUsernameValidation } from '../../../../hooks/useUsernameValidation';

// styles
import {
  MainContainer,
  SignUpContainer,
  AgreementText,
  VerificationContainer
} from './CreateAccount.styles.js';

// components
import InputBar from '../../../../components/input-bar/InputBar';
import InputsContainer from '../../../../components/inputs-container/InputsContainer';
import PrimaryBtn from '../../../../components/btns/primary-btn/PrimaryBtn';
import TeamSelection from './components/TeamSelection';
import CriticalMessage from '../../../../components/system/critical-message/CriticalMessage';

// MUI Icons
import { Error, CheckCircle, Info } from '@mui/icons-material';

export default function CreateAccount() {
  const dispatch = useDispatch();
  const teamIndex = useSelector(selectTeamIndex);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStrength, setPasswordStrength] = useState({ strength: '', checks: {} });
  const [isHoveringStrength, setIsHoveringStrength] = useState(false);
  const [usernameValidation, setUsernameValidation] = useState({ isValid: false, message: null, hasBeenValidated: false });
  const [emailValidation, setEmailValidation] = useState({ isValid: false, hasBeenValidated: false });
  const [showEmailTooltip, setShowEmailTooltip] = useState(false);
  const [confirmPasswordValidation, setConfirmPasswordValidation] = useState({ isValid: false, hasBeenValidated: false });
  const [showConfirmPasswordTooltip, setShowConfirmPasswordTooltip] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const { error, isPending, success, createAccount, sendEmailVerification } = useCreateAccount();
  const { validateUsername, isValidating: isValidatingUsername, validationResult } = useUsernameValidation();
  const criticalMessage = useSelector(selectCriticalMessage);

  useEffect(() => {
    dispatch(setCriticalMessage(null));
  }, [dispatch]);

  // Team selection is now handled by the TeamSelection component

  // Password strength evaluation
  const evaluatePasswordStrength = (password) => {
    if (!password) return { strength: '', checks: {} };
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const lengthValid = password.length >= 8;

    const score = [hasUpper, hasLower, hasNumber, hasSpecial, lengthValid].filter(Boolean).length;

    return {
      score,
      strength:
        score === 5 ? 'Strong' :
          score === 4 ? 'Good' :
            score === 3 ? 'Medium' :
              score === 2 ? 'Weak' : 'Very Weak',
      checks: {
        hasUpper,
        hasLower,
        hasNumber,
        hasSpecial,
        lengthValid
      }
    };
  };


  // Update password strength when password changes
  useEffect(() => {
    const strength = evaluatePasswordStrength(password);
    setPasswordStrength(strength);
  }, [password]);

  // Update username validation when validation result changes
  useEffect(() => {
    if (validationResult) {
      setUsernameValidation({
        isValid: validationResult.isAvailable,
        message: validationResult.message,
        hasBeenValidated: true
      });
    }
  }, [validationResult]);

  const handleUsernameChange = (value) => {
    const lowered = value.toLowerCase();
    setUsername(lowered);
    // Reset validation state when user edits the field
    setUsernameValidation({ isValid: false, message: null, hasBeenValidated: false });
  };

  const getUsernameFormatChecks = () => ({
    minLength: username.length >= 3,
    maxLength: username.length <= 20 && username.length > 0,
    validChars: username.length > 0 && /^[a-z0-9_]+$/.test(username),
  });

  // Handle username validation on blur
  const handleUsernameBlur = async () => {
    if (username.trim()) {
      await validateUsername(username);
    }
  };

  // Handle email validation on change
  const handleEmailChange = (value) => {
    setEmail(value);
    if (value.trim()) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      const isValid = emailRegex.test(value);
      setEmailValidation({
        isValid,
        hasBeenValidated: true
      });
    } else {
      setEmailValidation({
        isValid: false,
        hasBeenValidated: false
      });
    }
  };

  // Handle confirm password validation on change
  const handleConfirmPasswordChange = (value) => {
    setConfirmPassword(value);
    if (value.trim()) {
      const isValid = password && value === password;
      setConfirmPasswordValidation({
        isValid,
        hasBeenValidated: true
      });
    } else {
      setConfirmPasswordValidation({
        isValid: false,
        hasBeenValidated: false
      });
    }
  };

  const handleClick = async () => {
    // Clear any previous errors when starting a new attempt
    dispatch(setCriticalMessage(null));

    const passwordRequirements = {
      minLength: 8,
      upperCase: /[A-Z]/,
      lowerCase: /[a-z]/,
      numbers: /[0-9]/,
      specialChar: /[!@#$%^&*(),.?":{}|<>]/
    };

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!teamIndex) {
      dispatch(setSystemNotification('Please select a favorite team.'));
    } else if (!username) {
      dispatch(setSystemNotification('Please enter a username.'));
    } else if (!usernameValidation.hasBeenValidated) {
      // Force validation if not yet validated
      const validationResult = await validateUsername(username.toLowerCase().trim());
      if (!validationResult.isAvailable) {
        dispatch(setSystemNotification(validationResult.message || 'Please enter a valid username.'));
        return;
      }
    } else if (username && usernameValidation.hasBeenValidated && !usernameValidation.isValid) {
      dispatch(setSystemNotification('Please enter a valid username.'));
    } else if (!email) {
      dispatch(setSystemNotification('Please enter an email.'));
    } else if (!emailRegex.test(email)) {
      dispatch(setSystemNotification("Please enter a valid email."));
    } else if (!password) {
      dispatch(setSystemNotification('Please enter a password.'));
    } else if (password && !confirmPassword) {
      dispatch(setSystemNotification('Please confirm your password.'));
    } else if (password !== confirmPassword) {
      dispatch(setSystemNotification('Passwords do not match.'));
    }
    else if (
      password.length < passwordRequirements.minLength ||
      !passwordRequirements.upperCase.test(password) ||
      !passwordRequirements.lowerCase.test(password) ||
      !passwordRequirements.numbers.test(password) ||
      !passwordRequirements.specialChar.test(password)
    ) {
      dispatch(setSystemNotification('Password must be at least 8 characters long and include uppercase, lowercase, numbers, and special characters.'));
    }
    else {
      // Normalize username to lowercase for consistent storage
      const normalizedUsername = username.toLowerCase().trim();
      createAccount(email, password, normalizedUsername, teamIndex, null);
    }
  }

  useEffect(() => {
    setPassword('');
    setConfirmPassword('');
  }, [error]);

  // Handle showing verification screen after successful account creation
  useEffect(() => {
    if (success && !showVerification) {
      setShowVerification(true);
      setUsername('');
      setEmail('');
      setPassword('');
      setConfirmPassword('');
    }
  }, [success, showVerification]);

  // Reset verification screen if there's an error
  useEffect(() => {
    if (error && showVerification) {
      setShowVerification(false);
    }
  }, [error, showVerification]);


  const handleResendVerification = async () => {
    try {
      await sendEmailVerification();
      dispatch(setSystemNotification('Verification email sent! Check your inbox.'));
    } catch (err) {
      dispatch(setSystemNotification('Failed to send verification email. Please try again.'));
    }
  };

  // Check if all inputs are valid
  const isFormValid = () => {
    return (
      teamIndex && // Team is selected
      username && // Username is not empty
      usernameValidation.isValid && // Username is available
      email && // Email is not empty
      emailValidation.isValid && // Email format is valid
      password && // Password is not empty
      passwordStrength.strength === 'Strong' && // Password is strong
      confirmPassword && // Confirm password is not empty
      confirmPasswordValidation.isValid // Passwords match
    );
  };

  return (
    <SignUpContainer>
      {!showVerification && (
        <MainContainer>
          <TeamSelection />
          <InputsContainer>
            <div>
              <InputBar
                type='text'
                placeholder='Enter username...'
                label='Username'
                value={username}
                updateValue={handleUsernameChange}
                onBlur={handleUsernameBlur}
              />
              {username && (() => {
                const checks = getUsernameFormatChecks();
                const allFormatValid = checks.minLength && checks.maxLength && checks.validChars;
                const checkStyle = (pass) => ({ color: pass ? 'lightgreen' : '#aaa', margin: 0 });
                return (
                  <ul style={{ listStyle: 'none', padding: 0, margin: '6px 0 0 2px', fontSize: '12px' }}>
                    <li style={checkStyle(checks.minLength)}>
                      {checks.minLength ? '✓' : '○'} At least 3 characters
                    </li>
                    <li style={checkStyle(checks.maxLength)}>
                      {checks.maxLength ? '✓' : '○'} 20 characters or fewer
                    </li>
                    <li style={checkStyle(checks.validChars)}>
                      {checks.validChars ? '✓' : '○'} Letters, numbers, and underscores only
                    </li>
                    {allFormatValid && (
                      <li style={checkStyle(usernameValidation.isValid)}>
                        {isValidatingUsername
                          ? '… Checking availability'
                          : usernameValidation.hasBeenValidated
                            ? usernameValidation.isValid
                              ? '✓ Username is available'
                              : `✗ ${usernameValidation.message || 'Username is taken'}`
                            : '○ Tab away to check availability'
                        }
                      </li>
                    )}
                  </ul>
                );
              })()}
            </div>
            <div style={{ position: 'relative' }}>
              <InputBar
                type='email'
                placeholder='Enter email...'
                label='Email'
                value={email}
                updateValue={handleEmailChange}
              />
              {email && emailValidation.hasBeenValidated && (
                <div
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer'
                  }}
                  onMouseEnter={() => setShowEmailTooltip(true)}
                  onMouseLeave={() => setShowEmailTooltip(false)}
                >
                  {emailValidation.isValid ? (
                    <CheckCircle style={{ fontSize: '16px', color: '#4CAF50' }} />
                  ) : (
                    <Error style={{ fontSize: '16px', color: '#F44336' }} />
                  )}
                  {showEmailTooltip && (
                    <h3 style={{
                      position: 'absolute',
                      top: '-35px',
                      right: '0',
                      backgroundColor: '#333',
                      color: 'white',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      whiteSpace: 'nowrap',
                      zIndex: 1000,
                      pointerEvents: 'none',
                      margin: 0
                    }}>
                      {emailValidation.isValid ? 'Valid email format' : 'Invalid email format'}
                    </h3>
                  )}
                </div>
              )}
            </div>
            <div style={{ position: 'relative' }}>
              <InputBar type='password' placeholder='Enter password...' label='Password' value={password} updateValue={setPassword} showPasswordToggle />
              {password && (
                <div style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {passwordStrength.strength === 'Strong' ? (
                    <CheckCircle style={{
                      fontSize: '16px',
                      color: '#4CAF50'
                    }} />
                  ) : (
                    <Error style={{
                      fontSize: '16px',
                      color: passwordStrength.strength === 'Good' ? '#FFC107' :
                        passwordStrength.strength === 'Medium' ? '#FF9800' :
                          passwordStrength.strength === 'Weak' ? '#FF5722' : '#F44336'
                    }} />
                  )}
                </div>
              )}
            </div>
            {password && passwordStrength.strength !== 'Strong' && (
              <div>
                <div style={{
                  height: '6px',
                  width: '160px',
                  backgroundColor: '#eee',
                  borderRadius: '3px',
                  overflow: 'hidden',
                  marginBottom: '8px'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${(passwordStrength.score / 5) * 100}%`,
                    background: passwordStrength.score === 5 ? 'linear-gradient(to right, #4CAF50, #8BC34A)' :
                      passwordStrength.score === 4 ? 'linear-gradient(to right, #8BC34A, #FFC107)' :
                        passwordStrength.score === 3 ? 'linear-gradient(to right, #FFC107, #FF9800)' :
                          passwordStrength.score === 2 ? 'linear-gradient(to right, #FF9800, #F44336)' : '#F44336',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '12px',
                  color: '#666'
                }}>
                  <span>Password Strength:</span>
                  <span style={{
                    color: passwordStrength.strength === 'Good' ? '#FFC107' :
                      passwordStrength.strength === 'Medium' ? '#FF9800' :
                        passwordStrength.strength === 'Weak' ? '#FF5722' : '#F44336',
                    fontWeight: 'bold'
                  }}>
                    {passwordStrength.strength}
                  </span>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'help',
                      position: 'relative'
                    }}
                    onMouseEnter={() => setIsHoveringStrength(true)}
                    onMouseLeave={() => setIsHoveringStrength(false)}
                  >
                    <Info style={{
                      fontSize: '16px',
                      color: 'white'
                    }} />
                    {isHoveringStrength && (
                      <div style={{
                        position: 'absolute',
                        top: '120%',
                        right: '0',
                        padding: '8px',
                        zIndex: 10,
                        width: '160px',
                        backgroundColor: '#333',
                        borderRadius: '4px',
                        color: 'white',
                        fontSize: '11px'
                      }}>
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                          <li style={{ color: passwordStrength.checks.lengthValid ? 'lightgreen' : 'white' }}>
                            At least 8 characters
                          </li>
                          <li style={{ color: passwordStrength.checks.hasUpper ? 'lightgreen' : 'white' }}>
                            Contains uppercase letter
                          </li>
                          <li style={{ color: passwordStrength.checks.hasLower ? 'lightgreen' : 'white' }}>
                            Contains lowercase letter
                          </li>
                          <li style={{ color: passwordStrength.checks.hasNumber ? 'lightgreen' : 'white' }}>
                            Contains a number
                          </li>
                          <li style={{ color: passwordStrength.checks.hasSpecial ? 'lightgreen' : 'white' }}>
                            Contains special character
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {passwordStrength.strength === 'Strong' && (
              <div style={{ position: 'relative' }}>
                <InputBar
                  type='password'
                  placeholder='Enter confirm password...'
                  label='Confirm Password'
                  value={confirmPassword}
                  updateValue={handleConfirmPasswordChange}
                  showPasswordToggle
                />
                {confirmPassword && confirmPasswordValidation.hasBeenValidated && (
                  <div
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={() => setShowConfirmPasswordTooltip(true)}
                    onMouseLeave={() => setShowConfirmPasswordTooltip(false)}
                  >
                    {confirmPasswordValidation.isValid ? (
                      <CheckCircle style={{ fontSize: '16px', color: '#4CAF50' }} />
                    ) : (
                      <Error style={{ fontSize: '16px', color: '#F44336' }} />
                    )}
                    {showConfirmPasswordTooltip && (
                      <h3 style={{
                        position: 'absolute',
                        top: '-35px',
                        right: '0',
                        backgroundColor: '#333',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        zIndex: 1000,
                        pointerEvents: 'none',
                        margin: 0
                      }}>
                        {confirmPasswordValidation.isValid ? 'Password Match!' : 'Passwords do not match.'}
                      </h3>
                    )}
                  </div>
                )}
              </div>
            )}
            <PrimaryBtn
              onClick={handleClick}
              title={!isPending ? 'CREATE ACCOUNT' : 'LOADING...'}
              disabled={!isFormValid() || isPending}
              width={'fill'}
            />
            {(criticalMessage || error) &&
              <div className='critical-message-container'>
                <CriticalMessage text={criticalMessage || error} />
              </div>
            }
            <AgreementText><i>By continuing you agree to the</i> <b>CardVault Terms of Service</b><i>, and acknowledge you’ve read our </i><b>Privacy Policy</b>.</AgreementText>

          </InputsContainer>
        </MainContainer>
      )}
      {showVerification && (
        <VerificationContainer>
          <h3>Check your email for a verification link</h3>
          <p>If you don't see it, check your spam folder or request a new verification link.</p>
          <PrimaryBtn onClick={handleResendVerification} title='REQUEST NEW VERIFICATION LINK' />
        </VerificationContainer>
      )}
    </SignUpContainer>
  )
}